import React, { useEffect, useState } from "react";
import client, { apiErr } from "../../lib/api";
import { Panel, Spinner } from "../ui-kit";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Drawer, Field, Empty, inputCls, money } from "./shared";

const BLANK = { name: "", broker: "", mt5_id: "", funding: "", service: "Signals", phone: "", notes: "" };

function ClientForm({ initial, brokers, onSaved, onClose }) {
  const [f, setF] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name || !f.broker || !f.mt5_id) { toast.error("Name, broker and MT5 ID required"); return; }
    setBusy(true);
    try {
      const body = { ...f, funding: Number(f.funding || 0) };
      if (initial?.id) await client.patch(`/tm/clients/${initial.id}`, body);
      else await client.post("/tm/clients", body);
      toast.success("Client saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Drawer title={initial?.id ? "Edit Client" : "Add Client"} onClose={onClose} testid="client-drawer">
      <div className="space-y-3">
        <Field label="Client name"><input data-testid="client-name" value={f.name} onChange={set("name")} className={inputCls} /></Field>
        <Field label="Broker">
          <input data-testid="client-broker" list="tm-brokers" value={f.broker} onChange={set("broker")} className={inputCls} placeholder="e.g. Vantage" />
          <datalist id="tm-brokers">{brokers.map((b) => <option key={b} value={b} />)}</datalist>
        </Field>
        <Field label="MT5 ID"><input data-testid="client-mt5" value={f.mt5_id} onChange={set("mt5_id")} className={`${inputCls} font-mono`} /></Field>
        <Field label="Funding amount (USD)"><input data-testid="client-funding" type="number" value={f.funding} onChange={set("funding")} className={`${inputCls} font-mono`} /></Field>
        <Field label="Service">
          <select data-testid="client-service" value={f.service} onChange={set("service")} className={inputCls}>
            {["Signals", "Algo", "Both"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Phone (optional)"><input data-testid="client-phone" value={f.phone} onChange={set("phone")} className={inputCls} /></Field>
        <Field label="Notes (optional)"><textarea data-testid="client-notes" rows={3} value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
        <button data-testid="client-save-btn" disabled={busy} onClick={save} className="btn-gold w-full py-2.5 text-sm disabled:opacity-60">{busy ? "Saving…" : "Save Client"}</button>
      </div>
    </Drawer>
  );
}

export default function TeamClients() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const load = () => client.get("/tm/clients").then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    client.get("/tm/brokers").then(({ data }) => setBrokers(data.map((b) => b.name))).catch(() => {});
  }, []);
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await client.delete(`/tm/clients/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Panel testid="tm-clients-panel" title={`My Clients ${rows ? `(${rows.length})` : ""}`}
      right={<button data-testid="add-client-btn" onClick={() => setEdit({})} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><Plus size={14} />Add Client</button>}>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty testid="clients-empty" text="Abhi koi client nahi hai. 'Add Client' se pehla client add karein." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-widest text-slate-500 text-left">
              {["Name", "Broker", "MT5 ID", "Funding", "Service", ""].map((h) => <th key={h} className="px-4 py-2 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-[color:var(--border-subtle)] hover:bg-white/5 transition-colors duration-200">
                  <td className="px-4 py-2.5 text-white">{c.name}<div className="text-xs text-slate-500">{c.phone}</div></td>
                  <td className="px-4 py-2.5 text-slate-300">{c.broker}</td>
                  <td className="px-4 py-2.5 font-mono text-gold">{c.mt5_id}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-200">{money(c.funding)}</td>
                  <td className="px-4 py-2.5"><span className="text-[11px] px-2 py-0.5 border border-[color:var(--border-strong)] text-slate-300 uppercase">{c.service}</span></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`edit-client-${c.id}`} onClick={() => setEdit(c)} className="p-1.5 text-slate-400 hover:text-white"><Pencil size={14} /></button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => del(c)} className="p-1.5 text-slate-400 hover:text-bear"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <ClientForm initial={edit.id ? edit : null} brokers={brokers} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Panel>
  );
}
